import React from "react";
import {
  UseCaseConfig,
  FormConfigField,
  FIELD_TYPE,
  MEDIA_FIELD_TYPES,
  DEFAULT_CHECKLIST_OPTIONS,
} from "../shared/types";
import * as S from "../shared/styles";
import {
  OTHER_LABEL,
  allowsOther,
  correctionLabel,
  correctionOptions,
  optionDetail,
} from "../shared/helpers";
import InfoTip from "../shared/InfoTip";

// ----------------------------  FormPreview  ----------------------------

interface FormPreviewProps {
  config: UseCaseConfig;
  showFields?: boolean;
  showChecklist?: boolean;
}

const FormPreview: React.FC<FormPreviewProps> = ({
  config,
  showFields = true,
  showChecklist = true,
}) => {
  const fields = showFields
    ? [...config.fields].sort((a, b) => a.display_order - b.display_order)
    : [];
  const checklist = showChecklist
    ? [...config.checklist_items].sort((a, b) => a.display_order - b.display_order)
    : [];

  if (!fields.length && !checklist.length) {
    return (
      <p style={{ fontSize: "0.875rem", color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>
        {showFields ? "Add fields to see a preview." : "Add checklist items to see a preview."}
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {fields.length > 0 && (
        <div style={{ ...questionBanner, color: "#6b7280", fontStyle: "italic" }}>
          Question (loaded from each record)
        </div>
      )}

      {fields.map((f) => (
        <FieldPreviewCard key={f.id} field={f} />
      ))}

      {/* Checklist preview */}
      {checklist.length > 0 && (
        <div style={previewCard}>
          <div style={previewFieldLabel}>Checklist</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {checklist.map((item) => (
              <div key={item.id} style={checklistPreviewRow}>
                <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                  {item.label || "(untitled item)"}
                </span>
                <span style={{ display: "flex", gap: "4px" }}>
                  {(item.options ?? DEFAULT_CHECKLIST_OPTIONS).map((r, k) => (
                    <span key={k} style={checklistPill}>
                      {r}
                    </span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------------------  Field Preview Card  ----------------------------
// Shows: label + type badge, then the correction input preview (what the
// reviewer sees when they click Incorrect).  The field type drives this UI.

const FieldPreviewCard: React.FC<{ field: FormConfigField }> = ({ field }) => {
  const isMedia = MEDIA_FIELD_TYPES.indexOf(field.type) !== -1;
  const isCheckbox = field.type === FIELD_TYPE.CHECKBOX;
  const cbLabel = field.options[0]?.trim() || field.label || "Checkbox";

  return (
    <div style={previewCard}>
      {/* Label + type badge */}
      <div style={previewFieldLabel}>
        {field.label || "(untitled field)"}
        {field.mandatory && <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>}
        <span style={typeBadge}>{field.type.replace(/_/g, " ")}</span>
      </div>

      {isMedia ? (
        <div style={mediaPlaceholder}>{field.type.replace(/_/g, " ")}</div>
      ) : isCheckbox ? (
        <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "default" }}>
          <input type="checkbox" disabled style={{ width: "16px", height: "16px" }} />
          <span style={{ fontSize: "0.8125rem", color: "#6b7280" }}>{cbLabel}</span>
        </label>
      ) : (
        <CorrectionPreview field={field} />
      )}

      {/* Help text */}
      {field.help_text && (
        <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: "4px" }}>
          {field.help_text}
        </div>
      )}
    </div>
  );
};

// ----------------------------  CorrectionPreview  ----------------------------
// Mirrors the CorrectionInput in FieldReviewTable so admins can see exactly
// what the reviewer gets when they mark this field incorrect.

const CorrectionPreview: React.FC<{ field: FormConfigField }> = ({ field }) => {
  switch (field.type) {
    case FIELD_TYPE.DROPDOWN_SINGLE: {
      const opts = correctionOptions(field);
      if (opts.length === 0 && !allowsOther(field))
        return <span style={previewMuted}>Add options to preview the correction input.</span>;
      return (
        <fieldset style={corrFieldset}>
          <legend style={corrLegend}>
            {correctionLabel(field)}
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label key={o} style={radioRow}>
                <input type="radio" disabled style={{ marginTop: "2px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.8125rem", color: "#374151" }}>
                  {o}
                  <InfoTip text={optionDetail(field, o)} label={o} />
                </span>
              </label>
            ))}
            {allowsOther(field) && (
              <label style={radioRow}>
                <input type="radio" disabled style={{ marginTop: "2px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.8125rem", color: "#374151" }}>
                  {OTHER_LABEL} (reviewer enters text, required)
                </span>
              </label>
            )}
          </div>
        </fieldset>
      );
    }

    case FIELD_TYPE.DROPDOWN_MULTI: {
      const opts = correctionOptions(field);
      if (opts.length === 0 && !allowsOther(field))
        return <span style={previewMuted}>Add options to preview the correction input.</span>;
      return (
        <fieldset style={corrFieldset}>
          <legend style={corrLegend}>
            {correctionLabel(field)}
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label key={o} style={radioRow}>
                <input type="checkbox" disabled style={{ marginTop: "2px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.8125rem", color: "#374151" }}>
                  {o}
                  <InfoTip text={optionDetail(field, o)} label={o} />
                </span>
              </label>
            ))}
            {allowsOther(field) && (
              <label style={radioRow}>
                <input type="checkbox" disabled style={{ marginTop: "2px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.8125rem", color: "#374151" }}>
                  {OTHER_LABEL} (reviewer enters text, required)
                </span>
              </label>
            )}
          </div>
        </fieldset>
      );
    }

    case FIELD_TYPE.NUMERIC:
      return (
        <div>
          <div style={corrLabel}>
            {correctionLabel(field)}
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </div>
          <input
            type="number"
            disabled
            placeholder="Enter correct number"
            style={{ ...S.formInput, width: "200px", opacity: 0.7 }}
          />
        </div>
      );

    // text_input and everything else
    default:
      return (
        <div>
          <div style={corrLabel}>
            {correctionLabel(field)}
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </div>
          <textarea
            disabled
            placeholder="Explain the evidence and the correct interpretation"
            rows={field.multiline ? 3 : 2}
            style={{
              ...S.formInput,
              resize: "none" as const,
              minHeight: field.multiline ? "72px" : "48px",
              opacity: 0.7,
            }}
          />
        </div>
      );
  }
};

// ----------------------------  Styles  ----------------------------

const questionBanner: React.CSSProperties = {
  fontSize: "0.8rem",
  fontWeight: 600,
  color: "#1e3a5f",
  background: "#f0f7ff",
  borderRadius: "6px",
  padding: "8px 12px",
};

const previewCard: React.CSSProperties = {
  background: "white",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  padding: "12px",
};

const previewFieldLabel: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "8px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const typeBadge: React.CSSProperties = {
  fontSize: "0.6rem",
  fontWeight: 500,
  color: "#6b7280",
  background: "#f3f4f6",
  borderRadius: "4px",
  padding: "1px 6px",
  textTransform: "capitalize",
  marginLeft: "auto",
};

const corrFieldset: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "8px 10px",
  margin: 0,
};

const corrLegend: React.CSSProperties = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#6b7280",
  padding: "0 4px",
};

const corrLabel: React.CSSProperties = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#6b7280",
  marginBottom: "4px",
};

const radioRow: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "6px",
  padding: "3px 0",
  cursor: "default",
  lineHeight: 1.4,
};

const previewMuted: React.CSSProperties = {
  fontSize: "0.8125rem",
  color: "#9ca3af",
  fontStyle: "italic",
};

const mediaPlaceholder: React.CSSProperties = {
  background: "#f3f4f6",
  borderRadius: "6px",
  padding: "20px",
  textAlign: "center",
  fontSize: "0.75rem",
  color: "#9ca3af",
  textTransform: "capitalize",
};

const checklistPreviewRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const checklistPill: React.CSSProperties = {
  fontSize: "0.7rem",
  padding: "2px 8px",
  border: "1px solid #e5e7eb",
  borderRadius: "4px",
  color: "#9ca3af",
};

export default FormPreview;
